import { useState } from "react";
import { useFeedback } from "../../../contexts/FeedbackContext";
import { useFetch } from "../../../hooks/useFetch";
import { useProducts } from "../contexts/ProductsContext";
import { ProductDetails } from "../models/ProductDetails";
import { useLocalization } from "../../../contexts/LocalizationContext";
import { apiRoutes } from "../../../apiRoutes";
import { stringifyReplacer } from "../../../utils";

export const useProductsActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { request } = useFetch();
  const { onEditModeIdChange, getProducts } = useProducts();
  const { pushNotification, showModal } = useFeedback();
  const { translate } = useLocalization();

  const handleUpdate = async (productId: number, value: ProductDetails) => {
    setIsLoading(true);
    await request({
      route: apiRoutes.products.updateProduct(productId),
      body: JSON.stringify(value, stringifyReplacer),
      contentType: "application/json",
      method: "PUT",
      onSuccess: async () => {
        onEditModeIdChange(-1);
        await getProducts();
        pushNotification({ message: translate("request.products.updateSuccess"), type: "success" });
      },
      onFailure: () => pushNotification({ message: translate("request.products.updateFail"), type: "danger" }),
      onAny: () => setIsLoading(false)
    });
  };

  const handleCreate = async (value: ProductDetails) => {
    setIsLoading(true);
    await request({
      route: apiRoutes.products.createProduct(),
      body: JSON.stringify(value, stringifyReplacer),
      contentType: "application/json",
      method: "POST",
      onSuccess: async () => {
        onEditModeIdChange(-1);
        await getProducts();
        pushNotification({ message: translate("request.products.updateSuccess"), type: "success" });
      },
      onFailure: () => pushNotification({ message: translate("request.products.updateFail"), type: "danger" }),
      onAny: () => setIsLoading(false)
    });
  };

  const handleDelete = (productId: number) => {
    const handleContinue = async () => {
      showModal(null);
      setIsLoading(true);
      await request({
        route: apiRoutes.products.deleteProduct(productId),
        method: "DELETE",
        onSuccess: async () => {
          await getProducts();
          pushNotification({ message: translate("request.products.deleteSuccess"), type: "success" });
        },
        onFailure: () => pushNotification({ message: translate("request.products.deleteFail"), type: "danger" }),
        onAny: () => setIsLoading(false)
      });
    };

    showModal({
      isVisible: true,
      message: translate("plugins.removeThisProduct"),
      handleClose: () => showModal(null),
      handleContinue
    });
  };

  return {
    isLoading,
    handleUpdate,
    handleDelete,
    handleCreate
  };
};
